import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Checkbox } from './ui/checkbox';
import { Plus, Edit, Trash2, Save, Package, TrendingDown } from 'lucide-react';
import { useApi, useApiMutation } from '../hooks/useApi';
import { servicePackagesApi, serviceItemsApi } from '../services/api';
import { useToast } from '../hooks/use-toast';

const PackageManager = ({ companyId, onPackagesChange, onClose }) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [newPackage, setNewPackage] = useState({
    name: '',
    description: '',
    serviceIds: [],
    discountType: 'percentage',
    discountValue: 0
  });

  const { toast } = useToast();
  const { data: packages, loading, refetch } = useApi(
    () => servicePackagesApi.getAll(companyId),
    [companyId]
  );
  const { data: services } = useApi(
    () => serviceItemsApi.getAll(companyId),
    [companyId]
  );
  const { mutate: createPackage, loading: creating } = useApiMutation();
  const { mutate: updatePackage, loading: updating } = useApiMutation();
  const { mutate: deletePackage, loading: deleting } = useApiMutation();

  const handleCreatePackage = async () => {
    try {
      await createPackage(() => servicePackagesApi.create({
        ...newPackage,
        companyId: companyId
      }));
      
      toast({
        title: "Paket erstellt",
        description: "Das neue Service-Paket wurde erfolgreich erstellt.",
      });
      
      setIsCreateModalOpen(false);
      setNewPackage({
        name: '',
        description: '',
        serviceIds: [],
        discountType: 'percentage',
        discountValue: 0
      });
      refetch();
      
      // Notify parent component to reload packages
      if (onPackagesChange) {
        onPackagesChange();
      }
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Paket konnte nicht erstellt werden.",
        variant: "destructive",
      });
    }
  };

  const handleEditPackage = async () => {
    try {
      await updatePackage(() => servicePackagesApi.update(editingPackage.id, editingPackage));
      
      toast({
        title: "Paket aktualisiert",
        description: "Das Service-Paket wurde erfolgreich aktualisiert.",
      });
      
      setIsEditModalOpen(false);
      setEditingPackage(null);
      refetch();
      
      // Notify parent component to reload packages
      if (onPackagesChange) {
        onPackagesChange();
      }
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Paket konnte nicht aktualisiert werden.",
        variant: "destructive",
      });
    }
  };

  const handleDeletePackage = async (packageId) => {
    if (window.confirm('Sind Sie sicher, dass Sie dieses Paket löschen möchten?')) {
      try {
        await deletePackage(() => servicePackagesApi.delete(packageId));
        
        toast({
          title: "Paket gelöscht",
          description: "Das Service-Paket wurde erfolgreich gelöscht.",
        });
        
        refetch();
        
        // Notify parent component to reload packages
        if (onPackagesChange) {
          onPackagesChange();
        }
      } catch (error) {
        toast({
          title: "Fehler",
          description: "Paket konnte nicht gelöscht werden.",
          variant: "destructive",
        });
      }
    }
  };

  const openEditModal = (pkg) => {
    setEditingPackage({ ...pkg });
    setIsEditModalOpen(true);
  };

  const calculatePackageTotal = (serviceIds) => {
    if (!services) return 0;
    return serviceIds.reduce((total, serviceId) => {
      const service = services.find(s => s.id === serviceId);
      return total + (service ? service.price : 0);
    }, 0);
  };

  const calculateDiscountedPrice = (serviceIds, discountType, discountValue) => {
    const total = calculatePackageTotal(serviceIds);
    if (discountType === 'percentage') {
      return total * (1 - discountValue / 100);
    } else {
      return Math.max(0, total - discountValue);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8">Lade Service-Pakete...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Package className="w-6 h-6 text-blue-600" />
          <h2 className="text-2xl font-bold text-slate-800">Service-Pakete</h2>
        </div>
        
        <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
          <DialogTrigger asChild>
            <Button className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Neues Paket
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Neues Service-Paket erstellen</DialogTitle>
            </DialogHeader>
            <PackageForm
              package={newPackage}
              onChange={setNewPackage}
              onSave={handleCreatePackage}
              services={services || []}
              loading={creating}
            />
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {packages?.map((pkg) => (
          <PackageCard
            key={pkg.id}
            package={pkg}
            services={services || []}
            onEdit={openEditModal}
            onDelete={handleDeletePackage}
          />
        ))}
      </div>

      {packages?.length === 0 && (
        <Card className="p-8 text-center">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">Noch keine Service-Pakete</h3>
          <p className="text-gray-500 mb-4">
            Erstellen Sie Service-Pakete, um mehrere Leistungen mit Rabatt zu bündeln.
          </p>
          <Button onClick={() => setIsCreateModalOpen(true)}>
            Erstes Paket erstellen
          </Button>
        </Card>
      )}

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Service-Paket bearbeiten</DialogTitle>
          </DialogHeader>
          {editingPackage && (
            <PackageForm
              package={editingPackage}
              onChange={setEditingPackage}
              onSave={handleEditPackage}
              services={services || []}
              loading={updating}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const PackageCard = ({ package: pkg, services, onEdit, onDelete }) => {
  const packageServices = services.filter(s => pkg.serviceIds.includes(s.id));
  const totalPrice = packageServices.reduce((sum, service) => sum + service.price, 0);
  
  const discountedPrice = pkg.discountType === 'percentage' 
    ? totalPrice * (1 - pkg.discountValue / 100)
    : Math.max(0, totalPrice - pkg.discountValue);
  
  const savings = totalPrice - discountedPrice;
  const savingsPercentage = totalPrice > 0 ? (savings / totalPrice) * 100 : 0;

  return (
    <Card className="p-6 hover:shadow-lg transition-shadow">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-800">{pkg.name}</h3>
          <p className="text-sm text-slate-600 mt-1">{pkg.description}</p>
        </div>
        <Badge className="bg-green-100 text-green-800">
          <TrendingDown className="w-3 h-3 mr-1" />
          -{Math.round(savingsPercentage)}%
        </Badge>
      </div>

      <div className="space-y-2 mb-4">
        <h4 className="text-sm font-medium text-slate-700">Enthaltene Services:</h4>
        {packageServices.map(service => (
          <div key={service.id} className="flex justify-between text-sm">
            <span className="text-slate-600">{service.name}</span>
            <span className="text-slate-500">{service.price}€</span>
          </div>
        ))}
      </div>

      <div className="border-t pt-4 space-y-2">
        <div className="flex justify-between text-sm text-slate-500">
          <span>Einzelpreis:</span>
          <span className="line-through">{totalPrice.toLocaleString('de-DE')}€</span>
        </div>
        <div className="flex justify-between font-bold text-lg text-blue-600">
          <span>Paketpreis:</span>
          <span>{discountedPrice.toLocaleString('de-DE')}€</span>
        </div>
        <div className="flex justify-between text-sm text-green-600">
          <span>Ersparnis:</span>
          <span>{savings.toLocaleString('de-DE')}€</span>
        </div>
      </div>

      <div className="flex gap-2 mt-4">
        <Button size="sm" variant="outline" onClick={() => onEdit(pkg)} className="flex-1">
          <Edit className="w-3 h-3 mr-1" />
          Bearbeiten
        </Button>
        <Button size="sm" variant="outline" onClick={() => onDelete(pkg.id)} className="text-red-600">
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </Card>
  );
};

const PackageForm = ({ package: pkg, onChange, onSave, services, loading }) => {
  const handleServiceToggle = (serviceId, checked) => {
    const newServiceIds = checked 
      ? [...pkg.serviceIds, serviceId]
      : pkg.serviceIds.filter(id => id !== serviceId);
    
    onChange({ ...pkg, serviceIds: newServiceIds });
  };

  const totalPrice = pkg.serviceIds.reduce((sum, serviceId) => {
    const service = services.find(s => s.id === serviceId);
    return sum + (service ? service.price : 0);
  }, 0);

  const discountedPrice = pkg.discountType === 'percentage' 
    ? totalPrice * (1 - pkg.discountValue / 100)
    : Math.max(0, totalPrice - pkg.discountValue);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Paket-Name</Label>
          <Input
            value={pkg.name}
            onChange={(e) => onChange({ ...pkg, name: e.target.value })}
            placeholder="z.B. Business Paket"
          />
        </div>
        
        <div>
          <Label>Rabatt-Art</Label>
          <Select value={pkg.discountType} onValueChange={(value) => onChange({ ...pkg, discountType: value })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="percentage">Prozent (%)</SelectItem>
              <SelectItem value="fixed">Festbetrag (€)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>Beschreibung</Label>
        <Textarea
          value={pkg.description}
          onChange={(e) => onChange({ ...pkg, description: e.target.value })}
          placeholder="Beschreibung des Pakets..."
          rows={2}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Rabatt-Wert</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={pkg.discountValue}
            onChange={(e) => onChange({ ...pkg, discountValue: parseFloat(e.target.value) || 0 })}
          />
        </div>
        
        <div className="flex items-end">
          <div className="text-sm text-gray-600">
            {pkg.discountType === 'percentage' 
              ? `${pkg.discountValue}% Rabatt`
              : `${pkg.discountValue}€ Nachlass`
            }
          </div>
        </div>
      </div>

      <div>
        <Label className="text-base font-medium mb-3 block">Services auswählen:</Label>
        <div className="max-h-64 overflow-y-auto border rounded-lg p-3 space-y-2">
          {services.map((service) => (
            <div key={service.id} className="flex items-center space-x-3">
              <Checkbox
                checked={pkg.serviceIds.includes(service.id)}
                onCheckedChange={(checked) => handleServiceToggle(service.id, checked)}
              />
              <div className="flex-1">
                <span className="text-sm font-medium">{service.name}</span>
                <span className="text-sm text-gray-500 ml-2">({service.price}€)</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {pkg.serviceIds.length > 0 && (
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-medium text-blue-800 mb-2">Preisvorschau:</h4>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span>Einzelpreis gesamt:</span>
              <span className="line-through text-gray-500">{totalPrice.toLocaleString('de-DE')}€</span>
            </div>
            <div className="flex justify-between font-bold text-blue-600">
              <span>Paketpreis:</span>
              <span>{discountedPrice.toLocaleString('de-DE')}€</span>
            </div>
            <div className="flex justify-between text-green-600">
              <span>Ersparnis:</span>
              <span>{(totalPrice - discountedPrice).toLocaleString('de-DE')}€</span>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4">
        <Button onClick={onSave} disabled={loading || pkg.serviceIds.length === 0} className="flex items-center gap-2">
          <Save className="w-4 h-4" />
          {loading ? 'Speichere...' : 'Speichern'}
        </Button>
      </div>
    </div>
  );
};

export default PackageManager;